import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { SEO } from "../components/SEO";
import { useLang } from "../i18n/LanguageContext";

/* İleri Teknoloji — siyah zemin, altın kanji + dergi usulu tedavi görselleri akan akış sayfası. */

const FLOW = [
  { img: "/img/tech/proton.jpg", kanji: "陽子線", romaji: "Yōshisen", to: "/tedavi/proton",
    title: { tr: "Proton Terapisi", en: "Proton Therapy" },
    text: { tr: "Işın, tümörün derinliğinde durur; sağlıklı doku neredeyse hiç etkilenmez. Milimetrik hassasiyetin adı.", en: "The beam stops at the tumor's depth — healthy tissue stays almost untouched." } },
  { img: "/img/tech/bnct.jpg", kanji: "中性子", romaji: "Chūseishi", to: "/tedavi/bnct",
    title: { tr: "BNCT", en: "BNCT" },
    text: { tr: "Bor, hücre hücre hedefleme yapar. Işın değil, molekül düzeyinde seçicilik.", en: "Boron targets cell by cell — selectivity at the molecular level." } },
  { img: "/img/tech/heavyion.jpg", kanji: "重粒子", romaji: "Jūryūshi", to: "/tedavi/heavyion",
    title: { tr: "Ağır İyon (Karbon)", en: "Heavy Ion (Carbon)" },
    text: { tr: "Karbon iyonu, dirençli tümörlerde bile derin biyolojik etki bırakır. Japonya bu teknolojinin doğduğu yer.", en: "Carbon ions leave deep biological impact even in resistant tumors." } },
  { img: "/img/tech/cart.jpg", kanji: "細胞", romaji: "Saibō", to: "/tedavi/cart",
    title: { tr: "CAR-T Hücre Terapisi", en: "CAR-T Cell Therapy" },
    text: { tr: "Hastanın kendi bağışıklık hücreleri, tümörü tanıyacak şekilde yeniden programlanır.", en: "The patient's own immune cells, reprogrammed to recognize the tumor." } },
  { img: "/img/tech/ipsc.jpg", kanji: "再生", romaji: "Saisei", to: "/tedavi/ipsc",
    title: { tr: "iPSC — Yenileyici Tıp", en: "iPSC — Regenerative Medicine" },
    text: { tr: "Kyoto'nun Nobel'li keşfi: herhangi bir hücreden yeni doku. Araştırma aşamasındaki yarının tıbbı.", en: "Kyoto's Nobel-winning discovery: new tissue from any cell." } },
  { img: "/img/tech/vaccine.jpg", kanji: "免疫", romaji: "Meneki", to: "/tedavi/aftv",
    title: { tr: "Kişiye Özel Kanser Aşısı", en: "Personalized Cancer Vaccine" },
    text: { tr: "Tümörünüzün kendi hücrelerinden üretilen, yalnızca size ait immün eğitimi.", en: "Immune training made from your tumor's own cells — yours alone." } },
];

const fadeUp = { initial: { opacity: 0, y: 32 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-70px" }, transition: { duration: 0.8 } };

export default function TechPage() {
  const { lang } = useLang();
  const loc = (m) => m[lang] || m.tr;

  return (
    <div className="min-h-screen bg-[#0A0508]" data-testid="tech-page">
      <SEO title="İleri Teknoloji — Japonya'nın Tedavi Cephaneliği" description="Proton, BNCT, ağır iyon, CAR-T, iPSC ve kişiye özel aşı: Japonya'nın ileri kanser teknolojileri." path="/teknoloji" />
      <Navbar />

      <section className="relative overflow-hidden pt-36 pb-16 text-center">
        <span aria-hidden className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-magserif text-[55vw] lg:text-[32vw] leading-none text-[#C9A96A] opacity-[0.05] select-none pointer-events-none">医</span>
        <motion.p {...fadeUp} className="relative text-[11px] font-semibold tracking-[0.4em] text-[#C9A96A] uppercase">2D Healthcare × Japan</motion.p>
        <motion.h1 {...fadeUp} transition={{ duration: 0.9, delay: 0.1 }} className="relative mt-6 font-serif text-5xl sm:text-7xl font-bold tracking-tight text-[#F5EFE6]">
          {lang === "tr" ? "İleri Teknoloji" : "Advanced Technology"}
        </motion.h1>
        <motion.p {...fadeUp} transition={{ duration: 0.9, delay: 0.2 }} className="relative mt-6 max-w-xl mx-auto text-[#E8DCC8]/70 text-lg leading-relaxed px-6">
          {lang === "tr"
            ? "Japonya'nın üniversite hastanelerinde yan yana yürüyen altı ileri platform — her biri kendi kanjisiyle."
            : "Six advanced platforms side by side in Japan's university hospitals — each with its own kanji."}
        </motion.p>
      </section>

      <section className="relative max-w-6xl mx-auto px-6 pb-10 space-y-10 sm:space-y-16">
        {FLOW.map((item, i) => (
          <motion.div
            key={item.kanji}
            {...fadeUp}
            transition={{ duration: 0.8, delay: 0.05 }}
            data-testid={`tech-item-${i}`}
            className={`relative grid lg:grid-cols-2 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
          >
            <div className="rounded-3xl p-[2px] bg-[linear-gradient(140deg,#C9A96A_0%,#6e5522_45%,#C9A96A_65%,#8a6b2f_100%)] shadow-[0_30px_70px_-20px_rgba(0,0,0,0.8)]">
              <img src={item.img} alt={loc(item.title)} data-testid={`tech-image-${i}`} loading="lazy" className="w-full aspect-[4/5] object-cover rounded-[calc(1.5rem-2px)]" draggable={false} />
            </div>
            <div className="relative">
              <span aria-hidden className="absolute -top-14 -left-4 font-magserif text-[9rem] sm:text-[11rem] leading-none text-[#C9A96A] opacity-[0.12] select-none pointer-events-none">{item.kanji}</span>
              <div className="relative">
                <div className="font-magserif text-5xl text-[#C9A96A] leading-none">{item.kanji}</div>
                <div className="mt-3 text-[10px] font-semibold tracking-[0.3em] uppercase text-[#C9A96A]/60">{item.romaji}</div>
                <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-bold text-[#F5EFE6]">{loc(item.title)}</h2>
                <p className="mt-4 text-[#E8DCC8]/65 leading-relaxed max-w-md">{loc(item.text)}</p>
                <Link data-testid={`tech-link-${i}`} to={item.to} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#C9A96A] hover:text-[#dfc08a] transition-colors">
                  {lang === "tr" ? "Tedaviyi incele" : "Explore treatment"} <ArrowRight size={15} />
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </section>

      <section className="relative text-center py-24 px-6">
        <motion.div {...fadeUp}>
          <div className="font-magserif text-7xl text-[#C9A96A]">技</div>
          <p className="mt-6 max-w-md mx-auto text-[#E8DCC8]/70 leading-relaxed">
            {lang === "tr" ? "Teknoloji, zarafetle buluştuğunda şifaya dönüşür." : "Technology becomes healing when it meets grace."}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link data-testid="tech-to-wizard" to="/#sihirbaz" className="inline-flex items-center gap-2.5 h-12 px-8 rounded-full bg-[#C9A96A] text-[#1d0810] font-heading font-bold text-sm tracking-wide hover:bg-[#dfc08a] transition-colors">
              {lang === "tr" ? "Size Uygun Yolu Bulun" : "Find Your Path"} <ArrowRight size={16} />
            </Link>
            <Link data-testid="tech-to-zarafet" to="/zarafet" className="inline-flex items-center gap-2.5 h-12 px-8 rounded-full border border-[#C9A96A]/40 text-[#C9A96A] font-heading font-semibold text-sm hover:bg-[#C9A96A]/10 transition-colors">
              <ArrowLeft size={16} /> {lang === "tr" ? "Japon Zarafeti" : "Japanese Grace"}
            </Link>
          </div>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}
